import { z } from 'zod';

import { opMeta } from '../op-meta.ts';
import { numberCol } from '../schema/types.ts';
import { additiveUnit, preserveUnit, productUnit, quotientUnit } from '../schema/unit-propagation.ts';
import { cellNumber, UnitCell } from '../value.ts';
import { defineBinary, defineFold, defineNumeric } from './helpers.ts';

// Arithmetic.

// Numeric operand schema: a unit-bearing cell passes through intact (its
// number and unit are unwrapped at evaluation time by `defineFold`'s
// kernel); anything else keeps the strict coerce-to-number validation, so
// a genuinely non-numeric operand still fails eagerly with a typed error.
const numericParam = z.union([z.instanceof(UnitCell), z.coerce.number()]);

export const plus = z
  .object({
    name: z.literal('+'),
    params: numericParam.array(),
  })
  .describe('Sum of zero or more numbers. (+) → 0.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(+ 1 2 3)', result: '6' },
    ...defineFold<number>((vs) => vs.reduce((s, n) => s + n, 0), cellNumber, numberCol, additiveUnit),
  });

export const minus = z
  .object({
    name: z.literal('-'),
    params: numericParam.array().min(1),
  })
  .describe('Subtraction. (- a) → -a; (- a b c) → a - b - c.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(- 10 3 2)', result: '5' },
    ...defineFold<number>(
      (vs) => {
        const [first = 0, ...rest] = vs;
        if (rest.length === 0) {
          return -first;
        }
        return rest.reduce((acc, n) => acc - n, first);
      },
      cellNumber,
      numberCol,
      additiveUnit,
    ),
  });

export const times = z
  .object({
    name: z.literal('*'),
    params: numericParam.array(),
  })
  .describe('Product of zero or more numbers. (*) → 1.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(* 2 3 4)', result: '24' },
    ...defineFold<number>((vs) => vs.reduce((p, n) => p * n, 1), cellNumber, numberCol, productUnit),
  });

export const divide = z
  .object({
    name: z.literal('/'),
    params: numericParam.array().min(2),
  })
  .describe('Division chain. (/ a b c) → a / b / c.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(/ 24 2 3)', result: '4' },
    ...defineFold<number>(
      (vs) => {
        const [first = 0, ...rest] = vs;
        return rest.reduce((acc, n) => acc / n, first);
      },
      cellNumber,
      numberCol,
      quotientUnit,
    ),
  });

export const mod = z
  .object({
    name: z.literal('mod'),
    params: z.tuple([numericParam, numericParam]),
  })
  .describe('Remainder. (mod a b) → a mod b.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(mod 7 3)', result: '1' },
    ...defineBinary<number>((a, b) => a % b, cellNumber, numberCol, additiveUnit),
  });

export const pow = z
  .object({
    name: z.literal('pow'),
    params: z.tuple([z.unknown(), z.unknown()]),
  })
  .describe('Exponentiation. (pow base exp) → base ** exp.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(pow 2 10)', result: '1024' },
    ...defineNumeric((n) => (n[0] as number) ** (n[1] as number)),
  });

export const abs = z
  .object({ name: z.literal('abs'), params: z.tuple([z.unknown()]) })
  .describe('Absolute value.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(abs -5)', result: '5' },
    ...defineNumeric((n) => Math.abs(n[0] as number), preserveUnit),
  });

export const neg = z
  .object({ name: z.literal('neg'), params: z.tuple([z.unknown()]) })
  .describe('Arithmetic negation. (neg x) → -x.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(neg 5)', result: '-5' },
    ...defineNumeric((n) => -(n[0] as number), preserveUnit),
  });

export const round = z
  .object({ name: z.literal('round'), params: z.tuple([z.unknown()]) })
  .describe('Round to the nearest integer (half rounds toward +∞: (round -2.5) → -2).')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(round 2.5)', result: '3' },
    ...defineNumeric((n) => Math.round(n[0] as number), preserveUnit),
  });

export const floor = z
  .object({ name: z.literal('floor'), params: z.tuple([z.unknown()]) })
  .describe('Round toward -∞.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(floor 2.9)', result: '2' },
    ...defineNumeric((n) => Math.floor(n[0] as number), preserveUnit),
  });

export const ceil = z
  .object({ name: z.literal('ceil'), params: z.tuple([z.unknown()]) })
  .describe('Round toward +∞.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(ceil 2.1)', result: '3' },
    ...defineNumeric((n) => Math.ceil(n[0] as number), preserveUnit),
  });

export const sqrt = z
  .object({ name: z.literal('sqrt'), params: z.tuple([z.unknown()]) })
  .describe('Square root.')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(sqrt 9)', result: '3' },
    ...defineNumeric((n) => Math.sqrt(n[0] as number)),
  });

export const log = z
  .object({ name: z.literal('log'), params: z.tuple([z.unknown()]) })
  .describe('Natural logarithm (base e).')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(log 1)', result: '0' },
    ...defineNumeric((n) => Math.log(n[0] as number)),
  });

export const exp = z
  .object({ name: z.literal('exp'), params: z.tuple([z.unknown()]) })
  .describe('Exponential (e ** x).')
  .register(opMeta, {
    terseGroup: 'arith',
    example: { expr: '(exp 0)', result: '1' },
    ...defineNumeric((n) => Math.exp(n[0] as number)),
  });

export const minOp = z
  .object({
    name: z.literal('min'),
    params: z.array(z.unknown()).min(2),
  })
  .describe(
    'Scalar minimum of 2+ numbers (per-row, e.g. in derive/filter). NOTE: the 1-arg aggregate `(min col)` is a separate op in the aggregation context.',
  )
  .register(opMeta, {
    example: { expr: '(min 3 1 2)', result: '1' },
    ...defineNumeric((n) => Math.min(...n), additiveUnit),
  });

export const maxOp = z
  .object({
    name: z.literal('max'),
    params: z.array(z.unknown()).min(2),
  })
  .describe(
    'Scalar maximum of 2+ numbers (per-row, e.g. in derive/filter). NOTE: the 1-arg aggregate `(max col)` is a separate op in the aggregation context.',
  )
  .register(opMeta, {
    example: { expr: '(max 3 1 2)', result: '3' },
    ...defineNumeric((n) => Math.max(...n), additiveUnit),
  });
